import { useLocale } from '@portal/shared/hooks/useLocale';
import { formatDate } from '@portal/shared/utils/date';
import { assertNever } from '@portal/types/helpers';
import {
  getLocaleForAtlas,
  getOfferAvailabilityRestriction,
  getOfferInvalidReason
} from '@portal/shared/utils/offer/generic';

import { useTranslation } from 'react-i18next';
import {
  OFFER_INVALID_REASON,
  OFFER_MULTIPLE_OPTIONS_PLAN,
  OFFER_TRANSACTION,
  OfferPlanType,
  OfferTransactionType
} from '@ag1/atlas';
import { PortalOffer } from '@portal/types/offer';
import styles from './styles.module.scss';

const { FirstMonthFree, FreeTrial, Standard } = OFFER_MULTIPLE_OPTIONS_PLAN;

export const useOfferButtonDisabledReason = (offer: PortalOffer | null) => {
  const { t } = useTranslation('banners');
  const locale = useLocale();
  if (!offer) {
    return 'This offer is currently unavailable';
  }

  const invalidReason = getOfferInvalidReason(offer, locale);
  let invalidReasonText = '';
  switch (invalidReason) {
    case OFFER_INVALID_REASON.SoldOut: {
      invalidReasonText = t('offerSoldOut') || 'This offer has sold out';
      break;
    }
    case OFFER_INVALID_REASON.Expired: {
      const dateTo = getOfferAvailabilityRestriction(offer, getLocaleForAtlas(locale))?.date_to;
      if (!dateTo) {
        invalidReasonText = 'This offer expired and is no longer available';
        break;
      }
      const formattedDateTo = formatDate(new Date(dateTo), 'month_day_comma_year');
      invalidReasonText = `This offer expired on ${formattedDateTo}`;
      break;
    }
    case OFFER_INVALID_REASON.TooEarly: {
      const dateFrom = getOfferAvailabilityRestriction(offer, getLocaleForAtlas(locale))?.date_from;
      if (!dateFrom) {
        invalidReasonText = 'This offer is not available yet';
        break;
      }
      const formattedDateFrom = formatDate(new Date(dateFrom), 'month_day_comma_year');
      invalidReasonText = `This offer doesn't start until ${formattedDateFrom}`;
      break;
    }
    case OFFER_INVALID_REASON.Paused: {
      invalidReasonText = 'This offer is currently unavailable';
      break;
    }
    case OFFER_INVALID_REASON.UnsupportedLocale: {
      invalidReasonText = 'This offer is not valid in your region';
      break;
    }
    case null: {
      return null;
    }
    default: {
      invalidReasonText = assertNever(
        invalidReason as never,
        'This offer is currently unavailable'
      );
      break;
    }
  }
  if (!invalidReasonText.trim()) {
    return null;
  }
  return invalidReasonText;
};

/** Returns `null` if there's no button disabled reason. */
export const OfferButtonDisabledReason: React.FC<{
  style?: React.CSSProperties;
  offer: PortalOffer | null;
}> = ({ style, offer }) => {
  const buttonDisabledReason = useOfferButtonDisabledReason(offer || null);
  if (!buttonDisabledReason?.trim()) {
    return null;
  }

  return (
    <div className={styles.offerButtonDisabledReason} style={style}>
      {buttonDisabledReason}
    </div>
  );
};

export const useOfferHeadline = (
  offerType: OfferPlanType,
  transactionType: OfferTransactionType,
  defaultHeadline: string | null
): string => {
  const { t } = useTranslation('offer');

  if (transactionType !== OFFER_TRANSACTION.MultipleOptionsOffer) {
    return defaultHeadline || '';
  }

  switch (offerType) {
    case FirstMonthFree:
      return t('offerPrice.firstMonthFree');
    case FreeTrial:
    case Standard:
    default:
      return defaultHeadline || '';
  }
};
